import identity from "ramda/es/identity";
import isTruthy from "ramda-extension/es/isTruthy";
import { useMemo } from 'react';
import { useDisabledContext } from '../../../context/disabled';
import useDisabledSync from '../useDisabledSync';
import useFieldFocus from '../useFieldFocus';
import useFieldId from '../useFieldId';
import useFieldRegistration from '../useFieldRegistration';
import useFieldTransmission from '../useFieldTransmission';
import useHandleBlur from '../useHandleBlur';
import useHandleChange from '../useHandleChange';
import useLocalizedFieldName from '../useLocalizedFieldName';
import useValidateField from '../useValidateField';
/**
 * Provides everything for field
 *
 * @documented
 * @category Field
 * @import import useFullyFledgedField from '@gef-ui/form-core/hooks/field/useFullyFledgedField'
 * @param {Object} input
 * @param {String} input.fieldName
 * @param {Number[]} [input.fieldIndices] - indices for array, can be nested and sorted by path of nesting
 * @param {boolean} [input.initiallyDirty]
 * @param {function} [input.modelParser=Identity]
 * @param {function} [input.viewParser=Identity]
 * @param {boolean} [input.disabled=false]
 * @param {String} [input.defaultValue]
 * @param {Object} [options]
 * @param {Object} [options.transmissionExtension] - Passed extension to `useFieldTransmission`. See source code :)
 * @returns {Object} result
 * @returns {Function} result.handleBlur
 * @returns {Function} result.handleChange
 * @returns {String} result.validationType
 * @returns {Function} result.setaFocusFalse
 * @returns {Function} result.setaFocusTrue
 * @returns {Function} result.validateField
 * @returns {boolean} result.hasFocus
 * @returns {String} result.localizedLabel
 * @returns {String} result.fieldId
 * @returns {*} result.value
 * @returns {*} result.originalValue - value without viewParser
 * @returns {*} result....other - rest data from `useFieldTransmission`
 * @example
 *
 * 	const field = useFullyFledgedField({fieldName: 'xxx'});
 *
 */ const useFullyFledgedField = ({ fieldName, fieldIndices: unsafeIndices, initiallyDirty, modelParser, viewParser = identity, disabled: disabledByProp, defaultValue }, { transmissionExtension } = {})=>{
    // biome-ignore lint/correctness/useExhaustiveDependencies: expand
    const fieldIndices = useMemo(()=>unsafeIndices, unsafeIndices);
    const { validationType, value, asyncValidationInProgress, accessibleByValidation, visibleByValidation, ...other } = useFieldTransmission({
        fieldName,
        fieldIndices
    }, transmissionExtension);
    const disabledByForm = useDisabledContext();
    const disabled = disabledByForm || disabledByProp || isTruthy(asyncValidationInProgress) || !accessibleByValidation;
    const hidden = !visibleByValidation;
    const handleBlur = useHandleBlur({
        fieldName,
        fieldIndices
    });
    const validateField = useValidateField({
        fieldName,
        fieldIndices
    });
    // stary Field ma handleUpdate jako s model a changeChange bez model parseru
    const handleChange = useHandleChange({
        fieldName,
        fieldIndices,
        validationType,
        modelParser
    });
    const handleChangeNoParser = useHandleChange({
        fieldName,
        fieldIndices,
        validationType,
        modelParser: undefined
    });
    const { hasFocus, setFocusFalse, setFocusTrue } = useFieldFocus();
    const localizedLabel = useLocalizedFieldName({
        fieldName
    });
    const fieldId = useFieldId({
        fieldName,
        fieldIndices
    });
    useFieldRegistration({
        fieldName,
        fieldIndices,
        initiallyDirty,
        disabled,
        defaultValue
    });
    useDisabledSync({
        fieldName,
        fieldIndices,
        disabled
    });
    return {
        handleBlur,
        handleChange,
        handleChangeNoParser,
        validationType,
        setFocusFalse,
        setFocusTrue,
        value: hasFocus ? value : viewParser(value),
        originalValue: value,
        hasFocus,
        validateField,
        localizedLabel,
        fieldId,
        asyncValidationInProgress,
        disabled,
        hidden,
        ...other
    };
};
export default useFullyFledgedField;
